import { ExternalLink } from 'lucide-react';
import { Badge, Button, Card } from '@/components/ui';
import { getPostHogAdminInfo } from '@/lib/analytics';

function statusVariant(active: boolean): 'success' | 'warning' {
  return active ? 'success' : 'warning';
}

function formatBoolean(value: boolean): string {
  return value ? 'Enabled' : 'Disabled';
}

export function AdminPostHog() {
  const info = getPostHogAdminInfo();

  return (
    <div className="flex flex-col gap-6">
      <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="p-4 flex flex-col gap-2">
          <div className="text-sm text-muted-foreground">Analytics</div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">
              {formatBoolean(info.enabled)}
            </span>
            <Badge
              variant={statusVariant(info.enabled)}
              size="sm"
            >
              env
            </Badge>
          </div>
        </Card>
        <Card className="p-4 flex flex-col gap-2">
          <div className="text-sm text-muted-foreground">Configuration</div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">
              {info.configured ? 'Ready' : 'Missing'}
            </span>
            <Badge
              variant={statusVariant(info.configured)}
              size="sm"
            >
              token + host
            </Badge>
          </div>
        </Card>
        <Card className="p-4 flex flex-col gap-2">
          <div className="text-sm text-muted-foreground">SDK</div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">
              {info.initialized ? 'Initialized' : 'Idle'}
            </span>
            <Badge
              variant={statusVariant(info.initialized)}
              size="sm"
            >
              client
            </Badge>
          </div>
        </Card>
        <Card className="p-4 flex flex-col gap-2">
          <div className="text-sm text-muted-foreground">Session replay</div>
          <div className="flex items-center gap-2">
            <span className="text-xl font-semibold">
              {formatBoolean(info.sessionRecordingEnabled)}
            </span>
            <Badge
              variant="info"
              size="sm"
            >
              privacy
            </Badge>
          </div>
        </Card>
      </section>

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Card className="p-4 flex flex-col gap-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-lg font-semibold">Project</h2>
            {info.dashboardUrl ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => window.open(info.dashboardUrl!, '_blank')}
                rightIcon={<ExternalLink size={14} />}
              >
                Open PostHog
              </Button>
            ) : null}
          </div>
          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Project ID</dt>
              <dd className="font-mono text-right">{info.projectId ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Ingestion host</dt>
              <dd className="font-mono text-right">{info.host ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">App host</dt>
              <dd className="font-mono text-right">{info.appHost ?? '—'}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Token</dt>
              <dd className="font-mono text-right">
                {info.tokenPreview ?? '—'}
              </dd>
            </div>
          </dl>
        </Card>

        <Card className="p-4 flex flex-col gap-3">
          <h2 className="text-lg font-semibold">Capture policy</h2>
          <dl className="grid grid-cols-1 gap-2 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Autocapture</dt>
              <dd>{formatBoolean(info.autocaptureEnabled)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Manual pageviews</dt>
              <dd>{formatBoolean(info.manualPageviews)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Session replay</dt>
              <dd>{formatBoolean(info.sessionRecordingEnabled)}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-muted-foreground">Person properties</dt>
              <dd>Minimal</dd>
            </div>
          </dl>
        </Card>
      </section>

      <section className="flex flex-col gap-3">
        <h2 className="text-lg font-semibold">Event catalog</h2>
        <Card
          variant="default"
          padding="none"
        >
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-muted-foreground">
                  <th className="p-3 font-medium">Event</th>
                  <th className="p-3 font-medium">Trigger</th>
                  <th className="p-3 font-medium">Allowed properties</th>
                </tr>
              </thead>
              <tbody>
                {info.eventCatalog.map((event) => (
                  <tr
                    key={event.name}
                    className="border-b border-border last:border-b-0"
                  >
                    <td className="p-3 font-mono text-xs text-foreground">
                      {event.name}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {event.trigger}
                    </td>
                    <td className="p-3 text-muted-foreground">
                      {event.allowedProperties.length > 0
                        ? event.allowedProperties.join(', ')
                        : 'None'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>
    </div>
  );
}
